import { PageScheduler, TPageProps, TPageSequence, useApiPlay } from "@ancademy/vse-client";
import {
  AMap,
  BtnGroup,
  buildSubView,
  Em,
  FormWrapper,
  MapItemContent,
  Md,
  showMsg,
  TestPage,
  TItemData,
} from "@client";
import { Style } from "@client/style";
import { useAsyncEffect } from "@client/util";
import { css } from "@emotion/css";
import { NpcModel } from "@npc";
import {
  BargainStatus,
  calcScore,
  IBargainBuyerRecord,
  ISubCreateParams,
  ISubPlayerState,
  NumberUtil,
  Role,
  SceneConst,
  SubNamespace,
  TBargainShopList,
  TChat,
  waitSeconds,
} from "@share";
import {
  Button,
  Checkbox,
  Divider,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Menu,
  message,
  Modal,
  Space,
  Table,
  Tabs,
  Tag,
} from "antd";
import { TableProps } from "antd/lib/table/Table";
import _ from "lodash";
import React, { useEffect, useState } from "react";

const { Column } = Table,
  { TabPane } = Tabs;

namespace Help {
  export const MAX_CHAT_COUNT = 5;

  export const chatTableProps: TableProps<any> = {
    showHeader: false,
    bordered: false,
    size: "small",
    pagination: false,
  };

  export function getBargainBuyerRecord(
    bargain_shopList: TBargainShopList,
    sellerIndex: number,
    buyerIndex: number
  ): IBargainBuyerRecord {
    const { buyerRecordList } = bargain_shopList[sellerIndex];
    let buyerRecord: IBargainBuyerRecord = buyerRecordList.find((o) => o.buyerIndex == buyerIndex);
    if (!buyerRecord) {
      buyerRecord = {
        buyerIndex,
        status: BargainStatus.bargain,
        chat: [],
        tradePrice: null,
        utility: NumberUtil.uniformDist(SceneConst.bargain.utility),
      };
      buyerRecordList.push(buyerRecord);
    }
    return buyerRecord;
  }

  export function chatMsgList(role: Role, p: number = 0): Array<string> {
    return role === Role.buyer
      ? [`老板，便宜点喽，${p}可以吗？`, `再让一点儿吧，${p}。`, `${p}怎么样？考虑一下吧。`]
      : [`${p}，不能再低了`, `今天开张第一单，${p}给你了`, `低于${p}就亏本了`];
  }

  export function pageWithRole(role: Role, ...pageList: React.ComponentType<TPageProps>[]) {
    return pageList.map((Page) => (props: TPageProps) => {
      const { apiState } = useApiPlay<ISubPlayerState>(),
        skip = apiState.bargain_role !== role;
      useEffect(() => {
        if (skip) {
          props.nextPage();
        }
      }, []);
      return skip ? null : <Page {...props} />;
    });
  }
}

function ChooseRolePage({ nextPage }: TPageProps) {
  const { setApiState } = useApiPlay<ISubPlayerState>();
  return (
    <Modal {...Md.fnProps} footer={false} title="请选择你在本环节的角色">
      <Space
        size="large"
        className={css`
          width: 100%;
          justify-content: center;
        `}
      >
        {[
          {
            role: Role.buyer,
            style: Style.btnWarningSm,
            label: "买家",
          },

          {
            role: Role.seller,
            style: Style.btnPrimarySm,
            label: "卖家",
          },
        ].map(({ role, style, label }) => (
          <Button
            key={role}
            className={style}
            onClick={() => {
              setApiState((s) => (s.bargain_role = role));
              nextPage();
            }}
          >
            {label}
          </Button>
        ))}
      </Space>
    </Modal>
  );
}

namespace Seller {
  import getBargainBuyerRecord = Help.getBargainBuyerRecord;

  export function SetPricePage({ nextPage }: TPageProps) {
    const {
      apiState: { index },
      setApiState,
    } = useApiPlay<ISubPlayerState>();
    const [p1, setP1] = useState(null as number);
    useEffect(() => {
      (async () => {
        await showMsg("现在你作为销售人员，需要对公司的销售策略做出建议，你所负责的业务板块是家具销售");
        await showMsg(`先根据这一批家具的成本制定销售方案吧`);
      })();
    }, []);
    return (
      <FormWrapper>
        <Form.Item label="成本">
          <span>{SceneConst.bargain.cost}</span>
        </Form.Item>
        <Form.Item label="售价">
          <InputNumber
            min={SceneConst.bargain.price.MIN}
            max={SceneConst.bargain.price.MAX}
            value={p1}
            placeholder={(SceneConst.bargain.cost * 2).toString()}
            onChange={(val: number) => setP1(val)}
          />
        </Form.Item>
        <div style={{ textAlign: "center" }}>
          <Button
            className={Style.btnPrimarySm}
            onClick={() => {
              setApiState((s) =>
                s.bargain_shopList.push({
                  sellerIndex: index,
                  price: p1,
                  buyerRecordList: [],
                  closed: false,
                })
              );
              nextPage();
            }}
          >
            提交
          </Button>
        </div>
      </FormWrapper>
    );
  }

  export function PlayPage({ nextPage }: { nextPage: () => void }) {
    const {
        apiState: { index, bargain_shopList },
        setApiState,
      } = useApiPlay<ISubPlayerState>(),
      shop = bargain_shopList.find((m) => m.sellerIndex === index);
    const [activeBuyerIndex, setActiveBuyerIndex] = useState(null as number);
    useAsyncEffect(async () => {
      for (let i = 0; i < 30; i++) {
        const sellerIndex = ~~(Math.random() * bargain_shopList.length);
        const buyerIndex = i + bargain_shopList.length;
        setApiState((s) => {
          const buyerRecord = getBargainBuyerRecord(s.bargain_shopList, sellerIndex, buyerIndex);
          // 买家访问当前店铺时50%概率讨价还价，访问其它店铺时直接购买
          if (sellerIndex === index && Math.random() > 0.5) {
            const role = Role.buyer,
              price = SceneConst.bargain.price.MIN + ~~(Math.random() * 200);
            const chat = {
              role,
              price,
              msg: _.sample(Help.chatMsgList(role, price)),
            };
            buyerRecord.chat.push(chat);
          } else {
            buyerRecord.tradePrice = bargain_shopList[sellerIndex].price;
            buyerRecord.status = BargainStatus.traded;
          }
        });
        await waitSeconds(1);
      }
    }, []);
    return (
      <FormWrapper>
        <div
          className={css`
            display: flex;
            justify-content: center;
          `}
        >
          <MapItemContent
            data={[
              { label: "成本", value: SceneConst.bargain.cost },
              { label: "售价", value: shop.price },
            ]}
          />
        </div>
        <Tabs size="large" type="card">
          <TabPane tab="附近商铺概况" key="shopList">
            <Table size="small" pagination={false} dataSource={bargain_shopList}>
              <Column
                title="编号"
                render={({ sellerIndex }, __, i) => `卖家${i + 1}${sellerIndex == index ? "(你)" : ""}`}
              />
              <Column title="挂牌价" dataIndex="price" />
              <Column title="客流量" dataIndex="buyerRecordList" render={(list) => list.length} />
            </Table>
          </TabPane>
          <TabPane tab="顾客来访记录" key="buyerList">
            {shop.closed ? (
              <>
                <div
                  className={css`
                    font-size: 2rem;
                    text-align: center;
                    display: block;
                    opacity: 0.5;
                  `}
                >
                  已打烊
                </div>
                <div
                  className={css`
                    margin-top: 2rem;
                    text-align: center;
                  `}
                >
                  <Button className={Style.btnPrimarySm} onClick={() => nextPage()}>
                    下一步
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Table size={"small"} pagination={false} dataSource={shop.buyerRecordList}>
                  <Column title="买家编号" dataIndex="buyerIndex" render={(buyerIndex: number) => buyerIndex} />
                  <Column
                    title="期望价格"
                    dataIndex="chat"
                    render={(chat: TChat[]) => (
                      <span style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
                        {
                          chat
                            .slice()
                            .reverse()
                            .find((c) => c.role === Role.buyer)?.price
                        }
                      </span>
                    )}
                  />
                  <Column
                    title="状态"
                    dataIndex="status"
                    render={(status: BargainStatus) =>
                      ({
                        [BargainStatus.bargain]: <Tag color="blue">讨价还价中</Tag>,
                        [BargainStatus.traded]: <Tag color="green">交易成功</Tag>,
                        [BargainStatus.left]: <Tag>已离开</Tag>,
                      }[status])
                    }
                  />
                  <Column
                    title="砍价记录"
                    dataIndex="buyerIndex"
                    render={(buyerIndex: number) => (
                      <Button onClick={() => setActiveBuyerIndex(buyerIndex)} className={Style.btnPrimaryXS}>
                        查看
                      </Button>
                    )}
                  />
                </Table>
                {shop.buyerRecordList.map(({ buyerIndex }) => (
                  <SellerBargain
                    buyerIndex={buyerIndex}
                    visible={buyerIndex === activeBuyerIndex}
                    setVisible={(visible) => setActiveBuyerIndex(visible ? buyerIndex : null)}
                  />
                ))}
                <div style={{ marginTop: "1rem", textAlign: "center" }}>
                  <Button
                    danger
                    className={Style.btnPrimarySm}
                    onClick={async () => {
                      setApiState((s) => {
                        const shop = s.bargain_shopList.find((s) => s.sellerIndex === index);
                        shop.closed = true;
                        const tradePercent =
                          shop.buyerRecordList.filter((s) => s.tradePrice).length / (shop.buyerRecordList.length + 1);
                        s.result = {
                          score: calcScore(0, tradePercent, 1),
                        };
                      });
                      const { buyerRecordList } = shop,
                        tradeList = buyerRecordList.filter((o) => o.tradePrice),
                        totalProfit = tradeList
                          .map(({ tradePrice }) => tradePrice - SceneConst.bargain.cost)
                          .reduce((m, n) => m + n, 0);
                      await showMsg(
                        <p>
                          您本轮将商品定价为<Em>{shop.price}</Em>。共有
                          <Em>{buyerRecordList.length}</Em>位买家来访， 其中
                          <Em>{tradeList.length}</Em>位完成购买，累计获得收益
                          <Em>{totalProfit}</Em>。
                        </p>,
                        {
                          okText: "知道了",
                        }
                      );
                      nextPage();
                    }}
                  >
                    打烊
                  </Button>
                </div>
              </>
            )}
          </TabPane>
        </Tabs>
      </FormWrapper>
    );
  }

  function SellerBargain({
    buyerIndex,
    visible,
    setVisible,
  }: {
    buyerIndex: number;
    visible: boolean;
    setVisible: (visible: boolean) => void;
  }) {
    const {
      apiState: { index, bargain_shopList },
      setApiState,
    } = useApiPlay<ISubPlayerState>();
    const sellerIndex = bargain_shopList.findIndex((o) => o.sellerIndex === index);
    const { buyerRecordList } = bargain_shopList[sellerIndex],
      { status, tradePrice, chat: chatHistory } = buyerRecordList.find((o) => o.buyerIndex === buyerIndex),
      chatCount = chatHistory.filter((s) => s.role === Role.seller).length;
    const [chat, setChat] = useState({} as TChat);
    const rowStyle: React.CSSProperties = {
      margin: "1.5rem 0",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    };
    return (
      <Modal
        {...Md.pageProps}
        width={Md.fnWidth}
        visible={visible}
        cancelButtonProps={{ hidden: true }}
        onOk={() => setVisible(false)}
        title={
          status === BargainStatus.bargain && chatHistory[chatHistory.length - 1]?.role === Role.seller
            ? "砍价中，等待对方回应..."
            : "砍价记录"
        }
      >
        <Table {...Help.chatTableProps} dataSource={chatHistory}>
          <Column dataIndex="role" render={(role: Role) => role === Role.buyer && <Tag>买家</Tag>} />
          <Column
            dataIndex="msg"
            render={(msg, { role, price }: TChat, i) => (
              <div
                style={{
                  textAlign: role === Role.buyer ? "left" : "right",
                }}
              >
                <Em>{price}</Em>
                {msg}
                {i === chatHistory.length - 1 && role === Role.buyer && (
                  <Button
                    disabled={status !== BargainStatus.bargain}
                    type="link"
                    onClick={() => {
                      setApiState(async (s) => {
                        const chatIndex = i;
                        if (buyerIndex === index) {
                          s.tradeIndex = sellerIndex;
                        }
                        const buyerRecord = Help.getBargainBuyerRecord(s.bargain_shopList, sellerIndex, buyerIndex);
                        buyerRecord.tradePrice = buyerRecord.chat[chatIndex].price;
                        buyerRecord.status = BargainStatus.traded;
                        setVisible(false);
                      });
                    }}
                  >
                    成交
                  </Button>
                )}
              </div>
            )}
          />
          <Column dataIndex="role" render={(role: Role) => role === Role.seller && <Tag color="green">你</Tag>} />
        </Table>
        {status === BargainStatus.bargain ? (
          <div style={rowStyle}>
            报价：
            <InputNumber
              min={SceneConst.bargain.price.MIN}
              value={chat.price}
              onChange={(val) => setChat({ ...chat, price: +val })}
            />
            <Divider type="vertical" />
            消息：
            <Input
              style={{ width: "24rem" }}
              value={chat.msg}
              onChange={({ target: { value } }) => setChat({ ...chat, msg: value })}
            />
            <Divider type="vertical" />
            <Dropdown
              overlay={
                <Menu>
                  {Help.chatMsgList(Role.seller, chat.price).map((msg, i) => (
                    <Menu.Item key={i} onClick={() => setChat({ ...chat, msg })}>
                      {msg}
                    </Menu.Item>
                  ))}
                </Menu>
              }
            >
              <a>快捷消息</a>
            </Dropdown>
            <Divider type="vertical" />
            <Button
              className={Style.btnPrimarySm}
              disabled={chatCount >= Help.MAX_CHAT_COUNT}
              onClick={() => {
                if (!chat.price) {
                  return message.warn("请检查您的报价");
                }
                setApiState(async (s) => {
                  const buyerRecord = Help.getBargainBuyerRecord(s.bargain_shopList, sellerIndex, buyerIndex);
                  buyerRecord.status = BargainStatus.bargain;
                  buyerRecord.chat.push({ ...chat, role: Role.seller });
                });
              }}
            >
              发送({chatCount}/{Help.MAX_CHAT_COUNT})
            </Button>
          </div>
        ) : (
          <div style={{ marginTop: "2rem", textAlign: "center", fontSize: "1.2rem" }}>
            {status === BargainStatus.left ? (
              <>买家已离开</>
            ) : (
              <>
                交易成功，成交价格：<Em>{tradePrice}</Em>
              </>
            )}
          </div>
        )}
      </Modal>
    );
  }
}

namespace Buyer {
  export function PlayPage({ nextPage }: { nextPage: () => void }) {
    const {
      apiState: { index, bargain_shopList, tradeIndex, score },
      setApiState,
      game: {
        params: { searchCost },
      },
    } = useApiPlay<ISubPlayerState, ISubCreateParams>();
    const [seenList, setSeenList] = useState(bargain_shopList.map(() => false)),
      setSeen = (i: number) => {
        const _seenList = seenList.slice();
        _seenList[i] = true;
        setSeenList(_seenList);
      };
    const [activeItemIndex, setActiveItemIndex] = useState(null as number);
    useEffect(() => {
      (async () => {
        showMsg(
          `你的任务是在接下来的时间里，购买一套家具。\n
      目前有【${bargain_shopList.length}】家店铺正在在营业，过去看看吧`
        ).then();
      })();
    }, []);
    useEffect(() => {
      if (tradeIndex == null) {
        return;
      }
      if (!score) {
        setTimeout(
          () =>
            setApiState(async (s) => {
              const { utility } = Help.getBargainBuyerRecord(s.bargain_shopList, s.tradeIndex, index);
              s.result = {
                score: calcScore(SceneConst.bargain.utility.MIN, utility, SceneConst.bargain.utility.MAX),
              };
            }),
          500
        );
      }
      const { tradePrice } = bargain_shopList[tradeIndex].buyerRecordList.find((o) => o.buyerIndex === index);
      (async () => {
        const totalSearchCost = seenList.map((seen) => (seen ? searchCost : 0)).reduce((m, n) => m + n);
        await showMsg(
          <p>
            {!!totalSearchCost && (
              <>
                花费<Em>{totalSearchCost}</Em>的搜寻成本后，
              </>
            )}{" "}
            你买到一套心仪的家具，价格为
            <Em>{tradePrice}</Em>，恭喜你。
          </p>,
          {
            okText: "知道了",
          }
        );
        nextPage();
      })();
    }, [tradeIndex]);
    const items: TItemData[] = bargain_shopList.map(({ price }, i) => ({
      name: `店铺${String.fromCharCode("A".charCodeAt(0) + i)}：￥${price}`,
      seen: seenList[i],
      key: i,
    }));
    return (
      <>
        {bargain_shopList.map((_, sellerIndex) => (
          <BuyerBargain
            sellerIndex={sellerIndex}
            inShop={activeItemIndex === sellerIndex}
            leftShop={() => {
              setActiveItemIndex(null);
              setApiState(async (s) => {
                const buyerRecord = Help.getBargainBuyerRecord(s.bargain_shopList, sellerIndex, index);
                if (buyerRecord.status !== BargainStatus.traded) {
                  buyerRecord.status = BargainStatus.left;
                }
              });
            }}
          />
        ))}
        <AMap
          items={items}
          onClick={async (sellerIndex) => {
            if (tradeIndex != null) {
              return;
            }
            setSeen(sellerIndex);
            setActiveItemIndex(sellerIndex);
            setApiState((s) => {
              Help.getBargainBuyerRecord(s.bargain_shopList, sellerIndex, index);
            });
          }}
        />
      </>
    );
  }
  function BuyerBargain({
    sellerIndex,
    inShop,
    leftShop,
  }: {
    sellerIndex: number;
    inShop: boolean;
    leftShop: () => void;
  }) {
    const {
      apiState: { index, bargain_shopList },
      setApiState,
    } = useApiPlay<ISubPlayerState>();
    const { price, buyerRecordList, closed } = bargain_shopList[sellerIndex],
      buyerRecord = buyerRecordList.find((o) => o.buyerIndex === index);
    if (!buyerRecord) {
      return null;
    }
    const { tradePrice, chat: chatHistory, utility } = buyerRecord,
      chatCount = chatHistory.filter((s) => s.role === Role.buyer).length;
    const [showBargain, setShowBargain] = useState(false);
    const [chat, setChat] = useState({} as TChat);
    const rowStyle: React.CSSProperties = {
      margin: "1.5rem 0",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    };
    if (closed) {
      return (
        <Modal
          centered
          closable={false}
          visible={inShop}
          onOk={() => leftShop()}
          cancelButtonProps={{ hidden: true }}
          okText="确定"
        >
          本店已打烊，请明日再来
        </Modal>
      );
    }
    if (tradePrice) {
      return (
        <Modal
          {...Md.pageProps}
          width={Md.fnWidth}
          visible={inShop}
          onOk={() => leftShop()}
          cancelButtonProps={{ hidden: true }}
          title="交易成功"
        >
          成交价格：<Em>{tradePrice}</Em>
        </Modal>
      );
    }
    return (
      <Modal {...Md.pageProps} width={Md.fnWidth} visible={inShop} footer={false}>
        <div>
          <p style={{ fontSize: "1.2rem" }}>
            针对这套你心仪的家具，卖家的挂牌价为<Em>{price}</Em>
            ，对你而言其货币效用为<Em>{utility}</Em>你选择：
          </p>
          <BtnGroup>
            <Button className={Style.btnWarningSm} onClick={() => leftShop()}>
              离开
            </Button>
            <Button
              className={Style.btnPrimarySm}
              onClick={() => {
                setApiState(async (s) => {
                  s.tradeIndex = sellerIndex;
                  const buyerRecord = Help.getBargainBuyerRecord(s.bargain_shopList, sellerIndex, index);
                  buyerRecord.tradePrice = s.bargain_shopList[sellerIndex].price;
                  buyerRecord.status = BargainStatus.traded;
                });
              }}
            >
              购买
            </Button>
            <Button className={Style.btnPrimarySm} onClick={() => setShowBargain(!showBargain)}>
              砍价
            </Button>
          </BtnGroup>
          {showBargain && (
            <>
              <Divider>
                {chatHistory[chatHistory.length - 1]?.role === Role.buyer ? "砍价中，等待对方回应..." : "砍价记录"}
              </Divider>
              {chatHistory.length ? (
                <Table {...Help.chatTableProps} dataSource={chatHistory}>
                  <Column dataIndex="role" render={(role: Role) => role === Role.seller && <Tag>卖家</Tag>} />
                  <Column
                    dataIndex="msg"
                    render={(msg, { role, price }: TChat, i) => (
                      <div
                        style={{
                          textAlign: role === Role.seller ? "left" : "right",
                        }}
                      >
                        <Em>{price}</Em>
                        {msg}
                        {i === chatHistory.length - 1 && role === Role.seller && (
                          <Button
                            disabled={!!tradePrice}
                            type="link"
                            onClick={() => {
                              setApiState(async (s) => {
                                const buyerIndex = index;
                                if (buyerIndex === index) {
                                  s.tradeIndex = sellerIndex;
                                }
                                const buyerRecord = Help.getBargainBuyerRecord(
                                  s.bargain_shopList,
                                  sellerIndex,
                                  buyerIndex
                                );
                                buyerRecord.tradePrice = buyerRecord.chat[i].price;
                                buyerRecord.status = BargainStatus.traded;
                              });
                            }}
                          >
                            成交
                          </Button>
                        )}
                      </div>
                    )}
                  />
                  <Column
                    dataIndex="role"
                    render={(role: Role) => role === Role.buyer && <Tag color="green">你</Tag>}
                  />
                </Table>
              ) : null}

              <div style={rowStyle}>
                报价：
                <InputNumber
                  min={SceneConst.bargain.price.MIN}
                  value={chat.price}
                  onChange={(val) => setChat({ ...chat, price: +val })}
                />
                <Divider type="vertical" />
                消息：
                <Input
                  style={{ width: "24rem" }}
                  value={chat.msg}
                  onChange={({ target: { value } }) => setChat({ ...chat, msg: value })}
                />
                <Divider type="vertical" />
                <Dropdown
                  overlay={
                    <Menu>
                      {Help.chatMsgList(Role.buyer, chat.price).map((msg, i) => (
                        <Menu.Item key={i} onClick={() => setChat({ ...chat, msg })}>
                          {msg}
                        </Menu.Item>
                      ))}
                    </Menu>
                  }
                >
                  <a>快捷消息</a>
                </Dropdown>
                <Divider type="vertical" />
                <Button
                  className={Style.btnPrimarySm}
                  disabled={chatCount >= Help.MAX_CHAT_COUNT}
                  onClick={() => {
                    if (!chat.price) {
                      return message.warn("请检查您的报价");
                    }
                    setApiState((s) => {
                      const buyerRecord = Help.getBargainBuyerRecord(s.bargain_shopList, sellerIndex, index);
                      buyerRecord.status = BargainStatus.bargain;
                      buyerRecord.chat.push({
                        ...chat,
                        role: Role.buyer,
                      });
                    });
                    setTimeout(
                      () =>
                        setApiState((s) => {
                          const buyerRecord = Help.getBargainBuyerRecord(s.bargain_shopList, sellerIndex, index);
                          if (Math.random()) {
                            buyerRecord.status = BargainStatus.traded;
                            buyerRecord.tradePrice = chat.price;
                            s.tradeIndex = sellerIndex;
                          }
                        }),
                      2e3
                    );
                  }}
                >
                  发送({chatCount}/{Help.MAX_CHAT_COUNT})
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>
    );
  }
}

const pageSequence: TPageSequence = [
  ChooseRolePage,
  ...Help.pageWithRole(Role.seller, Seller.SetPricePage, Seller.PlayPage),
  ...Help.pageWithRole(Role.buyer, Buyer.PlayPage),
];

function Play({ playDone }: { playDone: () => void }) {
  const { game } = useApiPlay<ISubPlayerState>();
  return <PageScheduler gameId={game.id} schedulerKey="Play" pageSequence={pageSequence} onOver={playDone} />;
}

function Test({ done }: { done: (answers, answerScores) => void }) {
  return (
    <TestPage
      questions={[
        {
          Content: ({ inputProps }) => (
            <div>
              <p>搜寻过程讨价还价可能如何影响搜寻（10分，多项选择）</p>
              <Checkbox.Group {...inputProps({ margin: ".5rem" })}>
                <Checkbox value={1}>A.增加每一次搜寻的时间和成本</Checkbox>
                <br />
                <Checkbox value={2}>B.允许卖家使用更有弹性的价格策略</Checkbox>
                <br />
                <Checkbox value={3}>C.允许买家压低卖家产品价格</Checkbox>
                <br />
                <Checkbox value={4}>D.使消费者放弃搜寻市场</Checkbox>
              </Checkbox.Group>
            </div>
          ),
        },
        {
          Content: ({ inputProps }) => (
            <div>
              <p>
                搜寻过程中使用讨价还价后，卖方可以使用更有弹性的价格策略。比如，一位卖家可能在买家初始搜寻时，提出先买优惠的价格方案。然而在买家返回时，如果买家可以和卖家讨价还价，可能改变卖家的初始设置价格。这对搜寻市场可能的影响有哪些？（10分，多项选择）
              </p>
              <Checkbox.Group {...inputProps({ margin: ".5rem" })}>
                <Checkbox value={1}>A.搜寻市场解体，无人购买产品</Checkbox>
                <br />
                <Checkbox value={2}>B.出于竞争压力，卖方可能难以提价</Checkbox>
                <br />
                <Checkbox value={3}>C.卖方不一定能履行原有的价格承诺</Checkbox>
                <br />
                <Checkbox value={4}>D.买方不一定会相信卖家关于未来提升价格的“威胁”</Checkbox>
              </Checkbox.Group>
            </div>
          ),
        },
        {
          isAnalysis: true,
          Content: ({ inputProps }) => (
            <div>
              <p>
                你认为买卖双方在可以讨价还价以后，市场搜寻会受到什么样的影响？本题为主观题，无正确的答案，只需说明理由。
              </p>
              <br />
              <Input.TextArea maxLength={2e3} rows={4} {...inputProps({ width: "100%" })} />
            </div>
          ),
        },
      ]}
    />
  );
}

export const BargainView = buildSubView({
  namespace: SubNamespace.Bargain,
  label: "讨价还价",
  withSearch: true,
  npcModel: NpcModel.BuyCar,
  npcWelcome: async () => {
    await showMsg(`在本环节中，你可以自由选择想要扮演的角色，与其它参与者进行实时的沟通与交易`, {
      okText: "知道了",
    });
  },
  introMsg: () => [
    ...`1. 市场中有若干名参与者扮演买家和卖家的角色。你是【买家/卖家】
2. 卖家会在市场中开店并售卖一件抽象产品。而买家有一单位的产品需求，他们将在市场中搜寻合适的产品。
3. 卖家将选择产品的【挂价】，并且允许在后续时间与进店的买家讨价还价。
4. 在搜寻前，买家可以了解卖家产品的【挂价】，也就是卖家对外公开的价格。
5. 通过搜寻，买家可以了解产品对其的“货币效用”，并且有机会和卖家进行讨价还价。
`
      .trim()
      .split("\n"),
    <p>
      5.买家的货币效用将以一定的概率产生，其随机、均匀地分布在
      <Em>
        {SceneConst.bargain.utility.MIN}，{SceneConst.bargain.utility.MAX}
      </Em>
      之间。
    </p>,
    ...`
7. 买家可以选择的行动包括：（1）选择搜寻，付出搜寻成本，并且查看其他卖家的产品给该买家带来的货币效用；（2）与当前卖家互相交流并讨价还价；（3）按照（协议）价格购买卖家的产品。
8. 买家的搜寻成本为S_market_2。
9. 市场在所有卖家结束售卖时结束，买方的收益为其获得的产品货币效用减去支付的价格以及市场搜寻付出的搜寻成本，卖方的收益为产品出售价格与出售相应件数的合并加总。`
      .trim()
      .split("\n"),
  ],
  Play,
  Test,
  initState: (s) => {
    const shopList = Array(5)
      .fill(null)
      .map((_, i) => ({
        sellerIndex: i,
        price: NumberUtil.uniformDist(SceneConst.bargain.price),
        buyerRecordList: [],
        closed: false,
      }));
    s.bargain_shopList = shopList;
    s.index = shopList.length;
  },
});
